import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Footer from 'components/Footer';
import Header from 'components/Header';
import MetaPixel from 'components/MetaPixel';
import MicrosoftUetTag from 'components/MicrosoftUetTag';
import { RoutesComponent } from 'components/Routes/RoutesComponent';
import ScrollToTop from 'components/ScrollToTop';
import WhatsAppFloatingButton from 'components/WhatsAppFloatingButton';
import { useEffect, useMemo } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { BUILD_PROFILE } from 'utils/buildProfile';
import { shouldDisableTracking } from 'utils/env';
import { installExtraGoogleTag, installGoogleTag } from 'utils/googleTag';
import { isInAppBrowser } from 'utils/observability';

export const App: React.FC = () => {
  const brandTheme = BUILD_PROFILE.theme;
  const brandAssets = BUILD_PROFILE.assets;
  const googleTagId = BUILD_PROFILE.googleTagId;
  const googleAdsId = BUILD_PROFILE.googleAdsId;
  const isWebView = isInAppBrowser();

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: brandTheme.palette.primaryMain },
          secondary: { main: brandTheme.palette.secondaryMain },
          background: { default: brandTheme.palette.backgroundDefault },
          text: {
            primary: brandTheme.palette.textPrimary,
            secondary: brandTheme.palette.textSecondary,
          },
        },
        typography: {
          fontFamily: brandTheme.typography.fontFamily,
          h1: {
            fontFamily: brandTheme.typography.headingFontFamily,
            fontWeight: brandTheme.typography.headingWeight,
          },
          h2: {
            fontFamily: brandTheme.typography.headingFontFamily,
            fontWeight: brandTheme.typography.headingWeight,
          },
          h3: {
            fontFamily: brandTheme.typography.headingFontFamily,
            fontWeight: brandTheme.typography.headingWeight,
          },
          h4: {
            fontFamily: brandTheme.typography.headingFontFamily,
            fontWeight: brandTheme.typography.headingWeight,
          },
          h5: {
            fontFamily: brandTheme.typography.headingFontFamily,
            fontWeight: brandTheme.typography.headingWeight,
          },
          h6: {
            fontFamily: brandTheme.typography.headingFontFamily,
            fontWeight: brandTheme.typography.headingWeight,
          },
          body1: {
            fontFamily: brandTheme.typography.bodyFontFamily,
            fontWeight: brandTheme.typography.bodyWeight,
          },
          body2: {
            fontFamily: brandTheme.typography.bodyFontFamily,
            fontWeight: brandTheme.typography.bodyWeight,
          },
          button: { fontWeight: brandTheme.typography.buttonWeight, textTransform: 'none' },
        },
        shape: {
          borderRadius: BUILD_PROFILE.brand === 'chadebebe' ? 16 : 10,
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                border: `1px solid ${brandTheme.palette.borderSoft}`,
                boxShadow: '0 16px 30px rgba(28, 20, 14, 0.08)',
                borderRadius: BUILD_PROFILE.brand === 'chadebebe' ? 18 : 16,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: BUILD_PROFILE.brand === 'chadebebe' ? 14 : 12,
                letterSpacing: '0.12px',
              },
              containedPrimary: {
                boxShadow: `0 10px 22px ${brandTheme.palette.heroGlow}`,
              },
            },
          },
        },
      }),
    [brandTheme]
  );

  useEffect(() => {
    // Fora de produção (local ou staging) não instala tags de analytics/ads
    if (shouldDisableTracking()) return;
    installGoogleTag(googleTagId);
    installExtraGoogleTag(googleAdsId);
  }, [googleTagId, googleAdsId]);

  useEffect(() => {
    const headAssets = brandAssets.head;

    const setHref = (selector: string, href: string) => {
      const element = document.querySelector<HTMLLinkElement>(selector);
      if (element) {
        element.href = href;
      }
    };

    setHref('link[rel="icon"][sizes="96x96"]', headAssets.favicon96);
    setHref('link[rel="icon"][type="image/svg+xml"]', headAssets.faviconSvg);
    setHref('link[rel="shortcut icon"]', headAssets.faviconIco);
    setHref('link[rel="apple-touch-icon"][sizes="180x180"]', headAssets.appleTouchIcon);
    setHref('link[rel="apple-touch-icon"]:not([sizes])', headAssets.appIcon192);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeMeta) {
      themeMeta.content = headAssets.themeColor;
    }
  }, [brandAssets]);

  const handleOpenInBrowser = () => {
    if (typeof window === 'undefined') return;

    const currentUrl = window.location.href;
    const userAgent = navigator.userAgent.toLowerCase();

    if (/android/.test(userAgent)) {
      const url = new URL(currentUrl);
      const intentUrl = `intent://${url.host}${url.pathname}${url.search}${url.hash}#Intent;scheme=https;package=com.android.chrome;end`;
      window.location.href = intentUrl;
      return;
    }

    const openedWindow = window.open(currentUrl, '_blank', 'noopener,noreferrer');
    if (!openedWindow) {
      window.location.href = currentUrl;
    }
  };

  if (isWebView) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 2,
            background: `linear-gradient(145deg, ${brandTheme.palette.heroGradientStart} 0%, ${brandTheme.palette.heroGradientEnd} 100%)`,
          }}
        >
          <Paper
            elevation={4}
            sx={{
              width: '100%',
              maxWidth: 520,
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
              borderRadius: 4,
              border: `1px solid ${brandTheme.palette.borderSoft}`,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Box
                component="img"
                src={brandAssets.homeLogoSrc}
                alt={brandAssets.homeLogoAlt}
                sx={{ width: { xs: 180, sm: 220 }, maxWidth: '100%', height: 'auto' }}
              />

              <Stack spacing={1}>
                <Typography variant="body1" color="text.secondary">
                  Para uma melhor experiência, abra esta página no navegador do seu dispositivo.
                </Typography>
              </Stack>

              <Button variant="contained" size="large" onClick={handleOpenInBrowser} fullWidth>
                Abrir no navegador
              </Button>
            </Stack>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Router>
        <ScrollToTop />
        <MetaPixel />
        <MicrosoftUetTag />
        <Header />

        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <RoutesComponent />
        </Container>

        <Footer />
        <WhatsAppFloatingButton />
      </Router>
    </ThemeProvider>
  );
};
